import {SortOrder} from "../shared/enums/sortOrder";
import ResidentAccountCreateDetails from "../stores/domainStore/pages/models/residentAccountCreateDetails";
import {ResidentAccountDetail} from "../stores/domainStore/pages/models/residentAccountDetail";
import {IBackendCaller} from "./backendCaller";

const residentAdminServiceDefaultUrl = "/api/resident-admins";

/**
 * The interface for ResidentAdminService.
 */
export interface IResidentAdminService {
  /**
   * A GET call to resident admin API to get a list of residents.
   */
  searchResidents: (
    villageId?: string,
    pageNumber?: number,
    pageSize?: number,
    sortBy?: string,
    sortOrder?: SortOrder,
    searchTerm?: string,
    departmentTypeCode?: string
  ) => Promise<any>;

  /**
   * A GET call to User Service to get a list of all residents in a given village.
   */
  loadVillageResidents: (
    villageId?: string,
    departmentTypeCode?: string
  ) => Promise<any>;

  /**
   * A GET call to retrieve action-related counts for a village.
   */
  getResidentActions: (villageId: string) => Promise<{
    counts: {independent: number; serviced: number};
  }>;

  /**
   * A POST call to create a resident account.
   */
  createAccount: (
    resident: ResidentAccountCreateDetails
  ) => Promise<ResidentAccountDetail>;

  /**
   * A DELETE call to create a resident account.
   */
  removeAccount: (
    resident: ResidentAccountCreateDetails
  ) => Promise<ResidentAccountDetail>;

  /**
   * A POST call to change a resident's account email address.
   */
  changeEmail: (id: string, email: string) => Promise<any>;

  /**
   * A POST call to reset a resident's password (creates new temporary password).
   */
  resetPassword: (id: string) => Promise<ResidentAccountDetail>;
}

/**
 * Provides various calls to internal user API.
 */
export class ResidentAdminService implements IResidentAdminService {
  backendCaller: IBackendCaller;

  constructor(backendCaller: IBackendCaller) {
    this.backendCaller = backendCaller;
  }

  searchResidents = async (
    villageId?: string,
    pageNumber?: number,
    pageSize?: number,
    sortBy?: string,
    sortOrder?: SortOrder,
    searchTerm?: string,
    departmentTypeCode?: string
  ) => {
    if (villageId === undefined) {
      return {
        residents: [],
        totalCount: 0
      };
    }
    const encodedSearchTerm =
      searchTerm === undefined ? undefined : encodeURIComponent(searchTerm);
    const pageNumberQueryParam = pageNumber ? `&pageNumber=${pageNumber}` : "";
    const pageSizeQueryParam = pageSize ? `&pageSize=${pageSize}` : "";
    const sortByQueryParam = sortBy ? `&sortBy=${sortBy}` : "";
    const sortOrderQueryParam = sortOrder ? `&sortOrder=${sortOrder}` : "";
    const departmentTypeCodeQueryParam = departmentTypeCode
      ? `&departmentTypeCode=${departmentTypeCode}`
      : "";

    return await this.backendCaller.callApi(
      "GET",
      `${residentAdminServiceDefaultUrl}/residents?villageId=${villageId}${pageNumberQueryParam}${pageSizeQueryParam}${sortByQueryParam}${sortOrderQueryParam}&searchTerm=${encodedSearchTerm}${departmentTypeCodeQueryParam}`,
      null
    );
  };

  loadVillageResidents = async (
    villageId?: string,
    departmentTypeCode?: string
  ) => {
    if (villageId === undefined) {
      return {
        residents: [],
        totalCount: 0
      };
    }
    const departmentTypeCodeQueryParam = departmentTypeCode
      ? `?departmentTypeCode=${departmentTypeCode}`
      : "";
    return await this.backendCaller.callApi(
      "GET",
      `${residentAdminServiceDefaultUrl}/villages/${villageId}/residents${departmentTypeCodeQueryParam}`,
      null
    );
  };

  getResidentActions = async (villageId: string) => {
    return await this.backendCaller.callApi(
      "GET",
      `${residentAdminServiceDefaultUrl}/villages/${encodeURIComponent(villageId)}/actions`,
      null
    );
  };

  createAccount = async (
    resident: ResidentAccountCreateDetails
  ): Promise<ResidentAccountDetail> => {
    return await this.backendCaller.callApi(
      "POST",
      `${residentAdminServiceDefaultUrl}/residents:create-account`,
      JSON.stringify(resident)
    );
  };

  removeAccount = async (
    resident: ResidentAccountCreateDetails
  ): Promise<ResidentAccountDetail> => {
    return await this.backendCaller.callApi(
      "DELETE",
      `${residentAdminServiceDefaultUrl}/residents/${resident.residentId}:delete-account`
    );
  };

  changeEmail = async (
    id: string,
    email: string
  ): Promise<ResidentAccountDetail> => {
    var body = JSON.stringify({
      email: email
    });
    return await this.backendCaller.callApi(
      "POST",
      `${residentAdminServiceDefaultUrl}/residents/${id}:change-email`,
      body
    );
  };

  resetPassword = async (id: string): Promise<ResidentAccountDetail> => {
    return await this.backendCaller.callApi(
      "POST",
      `${residentAdminServiceDefaultUrl}/residents/${id}:reset-password`
    );
  };
}
